import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { MdUploadFile } from "react-icons/md"
import { MobergTheme } from "../../Components/MobergThemes/MobergColors"
import { MobergButton, MobergButtonShape, MobergButtonVariant } from "../../Components/MobergButton/MobergButton"
import { batchFiles } from "../Internal/claraOriginals"
import { LARGE_UPLOAD, UPLOAD_STATE, isActive, planUploads, sha256Hex, uploadQueue } from "./claraUploadQueue"
import {
	DropZone, HiddenInput, ModalFooter, ModalHint, ModalOverlay, ModalTitle, Muted, ProgressList, ProgressName,
	ProgressReason, ProgressRow, StatusTag, WideModal,
} from "./claraDocumentsStyles"

const CLARA_API = window.env?.REACT_APP_CLARA_API_URL

const STATE_LABEL = {
	[UPLOAD_STATE.CHECKING]: "Checking",
	[UPLOAD_STATE.WAITING]: "Waiting",
	[UPLOAD_STATE.UPLOADING]: "Uploading",
	[UPLOAD_STATE.RETRYING]: "Waiting",
	[UPLOAD_STATE.ATTACHING]: "Attaching",
	[UPLOAD_STATE.INDEXING]: "Indexing",
	[UPLOAD_STATE.READY]: "Ready",
	[UPLOAD_STATE.SKIPPED]: "Skipped",
	[UPLOAD_STATE.ATTACHED]: "Attached",
	[UPLOAD_STATE.FAILED]: "Failed",
}

const PILL = { ready: "success", attached: "success", failed: "danger", skipped: "neutral" }

export function useUploadItems() {
	const [items, setItems] = useState(uploadQueue.items())
	useEffect(() => {
		setItems(uploadQueue.items())
		return uploadQueue.subscribe(() => setItems(uploadQueue.items()))
	}, [])
	return items
}

function fmtSize(bytes) {
	if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

async function postJson(path, token, body) {
	const response = await fetch(`${CLARA_API}${path}`, {
		method: "POST",
		headers: { "Content-Type": "application/json", Authorization: token },
		body: JSON.stringify(body),
	})
	const data = await response.json().catch(() => ({}))
	if (!response.ok) throw new Error(data.error || `status ${response.status}`)
	return data
}

/* Hash, ask Clara which files she already has, then skip, attach or queue each one. Runs on
   after the dialog closes: every step reports to the module's queue, not to this component. */
export async function enqueueFiles(files, { workspace, getToken }) {
	const query = `workspace_name=${encodeURIComponent(workspace)}`
	const send = async file => {
		const body = new FormData()
		body.append("file", file)
		const response = await fetch(`${CLARA_API}/documents/upload?${query}`, { method: "POST", headers: { Authorization: getToken() }, body })
		return { status: response.status, data: await response.json().catch(() => ({})) }
	}
	const pdfs = files.filter(file => file.name.toLowerCase().endsWith(".pdf"))
	const others = files.filter(file => !pdfs.includes(file))
	uploadQueue.add(others, { send, workspace }).forEach(key => uploadQueue.update(key, { state: UPLOAD_STATE.FAILED, reason: "Only PDF files can be indexed" }))
	const keys = uploadQueue.add(pdfs, { send, workspace })

	// One file at a time, so a large pick is never all in memory. Without a secure context
	// there is no crypto.subtle; Clara still refuses duplicates itself.
	const hashes = []
	for (const file of pdfs) {
		hashes.push(window.crypto?.subtle ? await sha256Hex(file).catch(() => null) : null)
	}
	let existing = {}
	try {
		const known = hashes.filter(Boolean)
		for (let i = 0; i < known.length; i += 500) {
			const data = await postJson("/documents/check-hashes", getToken(), { workspace_name: workspace, hashes: known.slice(i, i + 500) })
			existing = { ...existing, ...data.existing }
		}
	} catch {
		existing = {}
	}

	const toAttach = []
	planUploads(pdfs, hashes, existing).forEach((plan, index) => {
		const key = keys[index]
		if (plan.action === "skip") uploadQueue.update(key, { state: UPLOAD_STATE.SKIPPED, documentId: plan.document.id, reason: `Already uploaded as "${plan.document.title}"` })
		else if (plan.action === "attach") toAttach.push({ key, file: plan.file, document: plan.document })
		else uploadQueue.start(key)
	})
	toAttach.forEach(({ key }) => uploadQueue.update(key, { state: UPLOAD_STATE.ATTACHING }))
	for (const batch of batchFiles(toAttach.map(entry => entry.file))) {
		const entries = toAttach.filter(entry => batch.includes(entry.file))
		try {
			const body = new FormData()
			batch.forEach(file => body.append("files", file))
			const response = await fetch(`${CLARA_API}/documents/originals?${query}`, { method: "POST", headers: { Authorization: getToken() }, body })
			const data = await response.json().catch(() => ({}))
			if (!response.ok) throw new Error(data.error || `status ${response.status}`)
			const attached = new Set((data.matched ?? []).map(match => match.document_id))
			entries.forEach(({ key, document }) => uploadQueue.update(key, attached.has(document.id)
				? { state: UPLOAD_STATE.ATTACHED, documentId: document.id, reason: `Attached the PDF to "${document.title}"` }
				: { state: UPLOAD_STATE.FAILED, reason: "The PDF could not be attached" }))
		} catch (e) {
			entries.forEach(({ key }) => uploadQueue.update(key, { state: UPLOAD_STATE.FAILED, reason: `Attaching failed: ${e.message}` }))
		}
	}
}

export function ClaraUploadDialog({ onClose, onPick, disabled }) {
	const items = useUploadItems()
	const input = useRef(null)
	const [over, setOver] = useState(false)
	const [lastPick, setLastPick] = useState(0)

	useEffect(() => {
		const onKeyDown = event => {
			if (event.key === "Escape") onClose()
		}
		window.addEventListener("keydown", onKeyDown)
		return () => window.removeEventListener("keydown", onKeyDown)
	}, [onClose])

	function take(fileList) {
		const files = Array.from(fileList ?? [])
		if (!files.length || disabled) return
		setLastPick(files.length)
		onPick(files)
	}

	const done = items.filter(item => !isActive(item)).length

	// On document.body, so the dim covers the app's sidebar and header too, not only the page.
	return createPortal(
		<ModalOverlay onClick={onClose}>
			<WideModal onClick={e => e.stopPropagation()} role="dialog" aria-label="Upload documents">
				<ModalTitle>Upload documents</ModalTitle>
				<ModalHint>
					PDF only. Pick or drop as many as you like. Clara reads each one's type; you only confirm the ones she is unsure of.
				</ModalHint>

				<DropZone
					type="button"
					$over={over}
					disabled={disabled}
					onClick={() => input.current?.click()}
					onDragOver={event => {
						event.preventDefault()
						setOver(true)
					}}
					onDragLeave={() => setOver(false)}
					onDrop={event => {
						event.preventDefault()
						setOver(false)
						take(event.dataTransfer.files)
					}}
				>
					<MdUploadFile size={22} />
					Choose PDFs or drop them here
				</DropZone>
				<HiddenInput
					ref={input}
					type="file"
					accept="application/pdf,.pdf"
					multiple
					onChange={event => {
						take(event.target.files)
						event.target.value = ""
					}}
				/>

				{lastPick > LARGE_UPLOAD && (
					<Muted>
						That is {lastPick} files. They upload two at a time and Clara indexes a few at once, so this takes a while; keep this
						tab open. For the PDFs of documents already uploaded, the server command python -m clara.backfill_originals is
						faster (docs/originals-backfill.md in the clara repo).
					</Muted>
				)}

				{items.length > 0 && (
					<>
						<Muted>{done} of {items.length} done. This list stays until you clear it or close the tab.</Muted>
						<ProgressList>
							{items.map(item => (
								<ProgressRow key={item.key}>
									<ProgressName title={item.name}>{item.name}</ProgressName>
									<Muted style={{ flexShrink: 0 }}>{fmtSize(item.size)}</Muted>
									{item.reason && <ProgressReason>{item.reason}</ProgressReason>}
									<StatusTag $tone={PILL[item.state] ?? "info"} style={{ alignSelf: "center" }}>{STATE_LABEL[item.state] ?? item.state}</StatusTag>
								</ProgressRow>
							))}
						</ProgressList>
					</>
				)}

				<ModalFooter>
					{done > 0 && (
						<MobergButton onClick={() => uploadQueue.clearFinished()} variant={MobergButtonVariant.OUTLINE} theme={MobergTheme.BLUE} shape={MobergButtonShape.RECTANGLE}>
							Clear finished
						</MobergButton>
					)}
					<MobergButton onClick={onClose} variant={MobergButtonVariant.FILLED} theme={MobergTheme.BLUE} shape={MobergButtonShape.RECTANGLE}>
						Close
					</MobergButton>
				</ModalFooter>
			</WideModal>
		</ModalOverlay>,
		document.body,
	)
}
